import { useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../api/client';
import { API_ITEMS_URL } from '../utils';
import { useOptionalCardDefinitions } from '../contexts/CardDefinitionsContext';
import { entryColumnFields, fieldKindFor, findField, isEntriesField, parseForBuilder } from '../utils/kqlBuilder';
import { createLookupResolver } from '../utils/lookups';
import { HEX_ID_RE, blockOf, isBlock } from '../utils/kql';
import { normalizeId } from '../utils/ids';
import { getUsersByIds } from '../services/users';
import { userDisplayName } from '../utils/userElements';

const BATCH_LIMIT = 100;
const EMPTY = [];

const isItemId = value => typeof value === 'string' && HEX_ID_RE.test(value.trim());

/** Every condition in a query, nested ones included. */
const walk = (node, visit) => {
	if (!node || typeof node !== 'object') return;
	if (node.op === 'cmp') { visit(node); return; }
	if (Array.isArray(node.args)) node.args.forEach(child => walk(child, visit));
	if (node.arg) walk(node.arg, visit);
};

/** The hex ids a query compares fields of `kind` against ('lookup' items, 'user' accounts). */
function queryIdsOfKind(query, fields, kind) {
	const { ast } = parseForBuilder(query);
	if (!ast) return EMPTY;
	const ids = new Set();
	// A condition inside a repeating group's block (KPR-683) is read in the
	// group's COLUMNS: a lookup column's item and a user column's person are
	// named in the chip like any other.
	const visit = vocabulary => (node) => {
		if (isBlock(node)) {
			const group = findField(vocabulary, node.path, !!node.forceElement, false);
			const inner = blockOf(node);
			if (isEntriesField(group) && inner && vocabulary === fields) walk(inner, visit(entryColumnFields(group)));
			return;
		}
		const field = findField(vocabulary, node.path, !!node.forceElement, !!node.facet);
		if (fieldKindFor(field?.dataType) !== kind) return;
		const values = node.value?.type === 'list' ? node.value.v : [node.value];
		values.forEach(entry => {
			if (entry && (entry.type === 'str' || entry.type === 'date') && isItemId(entry.v)) {
				ids.add(entry.v.trim());
			}
		});
	};
	walk(ast, visit(fields));
	return ids.size ? [...ids] : EMPTY;
}

/** The item ids a query compares lookup fields against. */
export function queryLookupIds(query, fields) {
	return queryIdsOfKind(query, fields, 'lookup');
}

/** The account ids a query compares user fields against. */
export function queryUserIds(query, fields) {
	return queryIdsOfKind(query, fields, 'user');
}

/**
 * The titles behind the hex ids in a filter.
 *
 * A lookup condition stores an item id — that is what makes it survive a
 * rename of the thing it points at — but a chip reading `resident is
 * 507f1f77bcf86cd799439011` tells nobody anything. So the ids in the query are
 * batch-resolved through the same `?ids=` endpoint the item feed uses and
 * labelled through the same `createLookupResolver`, which is what keeps a
 * filter chip, a tile field and the lookup input all saying "Molly Byrne".
 *
 * Ids are asked about once (a deleted or unreadable item must not become a
 * fetch on every keystroke); until an answer arrives `loading` is true and the
 * chip says "…" rather than flashing a hex string it is about to replace.
 */
export default function useQueryLookupTitles(query, fields) {
	const cardDefinitions = useOptionalCardDefinitions();
	const cardsById = cardDefinitions?.cardsById;
	const getElementsForCard = cardDefinitions?.getElementsForCard;

	const [itemsById, setItemsById] = useState({});
	const [usersById, setUsersById] = useState({});
	const [loading, setLoading] = useState(false);
	const attempted = useRef(new Set());
	const attemptedUsers = useRef(new Set());

	const ids = useMemo(() => queryLookupIds(query, fields), [query, fields]);
	const idsKey = ids.join(',');
	// A user condition stores an ACCOUNT id; the chip says the person's name,
	// through the same batch endpoint every avatar uses.
	const userIds = useMemo(() => queryUserIds(query, fields), [query, fields]);
	const userIdsKey = userIds.join(',');

	useEffect(() => {
		const wanted = userIdsKey.split(',').filter(Boolean).filter(id => !attemptedUsers.current.has(id));
		if (!wanted.length) return undefined;
		wanted.forEach(id => attemptedUsers.current.add(id));
		let active = true;
		getUsersByIds(wanted)
			.then(users => {
				if (!active || !users?.length) return;
				setUsersById(prev => {
					const next = { ...prev };
					users.forEach(user => {
						const id = normalizeId(user?._id);
						if (id) next[id] = user;
					});
					return next;
				});
			})
			.catch(() => {});
		return () => { active = false; };
	}, [userIdsKey]);

	useEffect(() => {
		const wanted = idsKey.split(',').filter(Boolean).filter(id => !attempted.current.has(id));
		if (!wanted.length) return undefined;
		wanted.forEach(id => attempted.current.add(id));
		let active = true;
		setLoading(true);
		const chunks = [];
		for (let i = 0; i < wanted.length; i += BATCH_LIMIT) chunks.push(wanted.slice(i, i + BATCH_LIMIT));
		Promise.all(chunks.map(chunk => api.get(API_ITEMS_URL, { params: { ids: chunk.join(',') } })
			.then(({ data }) => data || [])
			.catch(() => [])))
			.then(results => {
				if (!active) return;
				const fetched = results.flat();
				if (fetched.length) {
					setItemsById(prev => {
						const next = { ...prev };
						fetched.forEach(item => {
							const id = normalizeId(item?._id);
							if (id) next[id] = item;
						});
						return next;
					});
				}
			})
			.finally(() => { if (active) setLoading(false); });
		return () => { active = false; };
	}, [idsKey]);

	const resolve = useMemo(() => createLookupResolver({
		itemsById,
		cardsById,
		getElementsForCard: cardId => (typeof getElementsForCard === 'function' ? getElementsForCard(cardId) || [] : []),
	}), [itemsById, cardsById, getElementsForCard]);

	return useMemo(() => ({
		loading,
		/** The title for an id, '…' while it is still coming, undefined for anything else. */
		titleFor: (raw) => {
			if (!isItemId(raw)) return undefined;
			const id = String(raw).trim();
			if (usersById[id]) return userDisplayName(usersById[id]) || undefined;
			if (itemsById[id]) {
				const label = resolve(id);
				return label && label !== id ? label : undefined;
			}
			return loading ? '…' : undefined;
		},
	}), [itemsById, usersById, resolve, loading]);
}
